import React, { useState, useEffect } from 'react'
import { createUseStyles } from 'react-jss'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'

// import ErrorComponent from 'components/ErrorComponent'
import GeneralInputMask from 'components/GeneralInputMask'
import Toggle from 'components/Toggle'

import { currencyStringToFloat, currencyFloatToString } from 'helpers/currency'
import { PropertyAnnouncement } from 'types/propertyAnnouncement'
import StepButtons from '../../../../components/StepButtons'

import COLORS from 'styles/colors'
import { STEPS } from 'constants/announcementSteps'

const toggleLabels = {
  nonToggled: 'DONO PAGA',
  toggled: 'MORADOR PAGA',
}

type Props = {
  property: PropertyAnnouncement
  onClickSave: (payload: Partial<PropertyAnnouncement>, nextStep: string) => void
  onPressBack: () => void
}

const PropertyValues = (props: Props) => {
  const [error, setError] = useState<string>('')
  const [values, setValues] = useState(props.property.values)
  const [tenantPays, setTenantPays] = useState(props.property.tenant_pays)
  const [total, setTotal] = useState<number>(props.property.values.total)

  const classes = useStyles(props)

  const setValueForType = (type: string, value: any) => {
    setValues({ ...values, [type]: value })
  }

  const setTenantPaysForType = (type: string) => {
    setTenantPays({ ...tenantPays, [type]: !tenantPays[type] })
  }

  const handleSaveStep = () => {
    if (!values.rent) {
      setError('Aluguel é um campo obrigatório')
      return
    }

    setError('')

    const payload = {
      tenant_pays: { ...tenantPays },
      values: {
        rent: currencyStringToFloat(values.rent),
        condo: currencyStringToFloat(values.condo),
        iptu: currencyStringToFloat(values.iptu),
        other: currencyStringToFloat(values.other),
        total: total,
      },
    }

    props.onClickSave(payload, STEPS.WARRANTY)
  }

  const getToggleLabel = (toggled: boolean) =>
    toggled ? toggleLabels.toggled : toggleLabels.nonToggled

  const currencyMask = createNumberMask({
    prefix: 'R$ ',
    allowDecimal: true,
    decimalSymbol: ',',
    thousandsSeparatorSymbol: '.',
  })

  const setTotalValue = () => {
    const localTotal = Object.keys(values).reduce((acc, currentValue) => {
      if (tenantPays[currentValue]) {
        acc += currencyStringToFloat(values[currentValue])
      }
      return acc
    }, 0)

    setTotal(localTotal)
  }

  const useMountEffect = (fun, arr) => useEffect(fun, arr)

  useMountEffect(setTotalValue, [values, tenantPays])

  return (
    <div>
      {/* TODO: set errors when submiting */}
      {/* <ErrorComponent errors={errors} /> */}
      <div>
        <GeneralInputMask
          mask={currencyMask}
          value={values.rent || ''}
          label='aluguel'
          handleChange={(e) => {
            !!error && setError('')
            setValueForType('rent', e.target.value)
          }}
          error={error}
        />
        <p className={classes.observationText}>
          Esse é o valor liquido que você receberá na sua conta todo mês. Caso você opte pelo seguro
          o valor será aplicado na próxima etapa.
        </p>
      </div>
      <div className={classes.valueRow}>
        <GeneralInputMask
          value={values.condo || ''}
          label='Condomínio'
          mask={currencyMask}
          handleChange={(e) => setValueForType('condo', e.target.value)}
        />
        <Toggle
          toggled={tenantPays.condo}
          label={getToggleLabel(tenantPays.condo)}
          id='condo'
          onClick={() => setTenantPaysForType('condo')}
        />
      </div>
      <div className={classes.valueRow}>
        <GeneralInputMask
          value={values.iptu || ''}
          mask={currencyMask}
          label='IPTU'
          handleChange={(e) => setValueForType('iptu', e.target.value)}
        />
        <Toggle
          toggled={tenantPays.iptu}
          label={getToggleLabel(tenantPays.iptu)}
          id='IPTU'
          onClick={() => setTenantPaysForType('iptu')}
        />
      </div>
      <div className={classes.valueRow}>
        <GeneralInputMask
          value={values.other || ''}
          mask={currencyMask}
          label='Outros'
          handleChange={(e) => setValueForType('other', e.target.value)}
        />
        <Toggle
          toggled={tenantPays.other}
          label={getToggleLabel(tenantPays.other)}
          id='other'
          onClick={() => setTenantPaysForType('other')}
        />
      </div>
      <div className={classes.totalBox}>
        <p className={classes.totalText}>VALOR TOTAL:</p>
        <p className={classes.totalValueText}>{currencyFloatToString(total)}</p>
      </div>
      <StepButtons onPressNext={handleSaveStep} onPressBack={props.onPressBack} />
    </div>
  )
}

const useStyles = createUseStyles({
  observationText: {
    fontSize: 13,
    margin: '8px',
  },
  autocompleteContainer: {
    margin: '16px 0 32px',
  },
  valueRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalBox: {
    margin: '0 auto',
    marginTop: 16,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: `2px solid ${COLORS.brand.primaryDark}`,
    borderRadius: 4,
    padding: '25px 20px',
  },
  totalText: {
    textTransform: 'uppercase',
    fontWeight: 600,
    color: COLORS.brand.primaryDark,
    fontSize: 16,
  },
  totalValueText: {
    textTransform: 'uppercase',
    fontWeight: 600,
    color: COLORS.brand.primaryDark,
    fontSize: 22,
  },
})

export default PropertyValues
